import { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { 
  Upload, 
  Download, 
  Trash2, 
  FileText, 
  Image, 
  File,
  Camera,
  Receipt,
  Map,
  FolderOpen,
  ImagePlus,
  Eye
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';

interface ParteTrabajoDocumento {
  id: string;
  parte_trabajo_id: string;
  nombre_archivo: string;
  tipo_documento: string;
  url_archivo: string;
  tamaño_archivo: number;
  descripcion?: string;
  created_at: string;
}

interface ParteTrabajoDocumentosProps {
  parteTrabajoId: string;
  readonly?: boolean;
}

const TIPOS_DOCUMENTO = [
  { value: 'foto_antes', label: 'Foto antes del trabajo', icon: Camera },
  { value: 'foto_despues', label: 'Foto después del trabajo', icon: Camera },
  { value: 'foto_trabajo', label: 'Foto durante el trabajo', icon: Camera },
  { value: 'documento', label: 'Documento adjunto', icon: FileText },
  { value: 'plano', label: 'Plano/esquema', icon: Map },
  { value: 'factura', label: 'Factura/recibo', icon: Receipt },
  { value: 'otro', label: 'Otro', icon: File },
];

const getStoragePath = (urlOrPath: string): string | null => {
  if (!urlOrPath) return null;
  const marker = '/parte-trabajo-documentos/';
  const idx = urlOrPath.indexOf(marker);
  if (idx >= 0) return urlOrPath.substring(idx + marker.length).split('?')[0];
  if (!urlOrPath.startsWith('http')) return urlOrPath;
  return null;
};

export const ParteTrabajoDocumentos: React.FC<ParteTrabajoDocumentosProps> = ({ 
  parteTrabajoId, 
  readonly = false 
}) => {
  const [documentos, setDocumentos] = useState<ParteTrabajoDocumento[]>([]);
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [tipoDocumento, setTipoDocumento] = useState<string>('foto_trabajo');
  const [descripcion, setDescripcion] = useState<string>('');
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (parteTrabajoId) {
      fetchDocumentos();
    }
  }, [parteTrabajoId]);

  const fetchDocumentos = async () => {
    try {
      const { data, error } = await supabase
        .from('parte_trabajo_documentos')
        .select('*')
        .eq('parte_trabajo_id', parteTrabajoId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      const docs = data || [];
      setDocumentos(docs);

      const map: Record<string, string> = {};
      await Promise.all(
        docs.map(async (d) => {
          const path = getStoragePath(d.url_archivo);
          if (!path) return;
          const { data: signed } = await supabase.storage
            .from('parte-trabajo-documentos')
            .createSignedUrl(path, 3600);
          if (signed?.signedUrl) map[d.id] = signed.signedUrl;
        })
      );
      setSignedUrls(map);
    } catch (error) {
      console.error('Error fetching documentos:', error);
      toast({
        title: "Error",
        description: "Error al cargar los documentos",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !tipoDocumento) return;

    setUploading(true);
    try {
      const { compressImageForUpload } = await import('@/lib/imageUtils');
      setOptimizing(true);
      const uploadFile = await compressImageForUpload(file);
      setOptimizing(false);
      const fileExt = uploadFile.name.split('.').pop();
      const fileName = `${parteTrabajoId}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('parte-trabajo-documentos')
        .upload(fileName, uploadFile, { contentType: uploadFile.type });

      if (uploadError) throw uploadError;

      const { error: insertError } = await supabase
        .from('parte_trabajo_documentos')
        .insert({
          parte_trabajo_id: parteTrabajoId,
          nombre_archivo: file.name,
          tipo_documento: tipoDocumento as any,
          url_archivo: fileName,
          tamaño_archivo: uploadFile.size,
          descripcion: descripcion || null,
        });

      if (insertError) throw insertError;


      toast({
        title: "Documento subido",
        description: "El documento se ha subido correctamente",
      });

      fetchDocumentos();
      setDescripcion('');
      if (cameraInputRef.current) cameraInputRef.current.value = '';
      if (galleryInputRef.current) galleryInputRef.current.value = '';
    } catch (error) {
      console.error('Error uploading file:', error);
      toast({
        title: "Error",
        description: "Error al subir el documento",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const getMimeType = (filename: string): string => {
    const ext = filename.toLowerCase().split('.').pop() || '';
    const map: Record<string, string> = {
      jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif',
      webp: 'image/webp', bmp: 'image/bmp', pdf: 'application/pdf',
      doc: 'application/msword', docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      txt: 'text/plain',
    };
    return map[ext] || 'application/octet-stream';
  };

  const handleView = async (documento: ParteTrabajoDocumento) => {
    const url = signedUrls[documento.id];
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleDownload = async (documento: ParteTrabajoDocumento) => {
    try {
      const path = getStoragePath(documento.url_archivo);
      if (!path) throw new Error('Ruta no válida');
      const { data, error } = await supabase.storage
        .from('parte-trabajo-documentos')
        .download(path);
      if (error || !data) throw error || new Error('Sin datos');
      const mime = getMimeType(documento.nombre_archivo);
      const blob = new Blob([await data.arrayBuffer()], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = documento.nombre_archivo;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading file:', error);
      toast({
        title: "Error",
        description: "Error al descargar el documento",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (documento: ParteTrabajoDocumento) => {
    try {
      const filePath = getStoragePath(documento.url_archivo);
      if (filePath) {
        await supabase.storage
          .from('parte-trabajo-documentos')
          .remove([filePath]);
      }

      // Delete record
      const { error } = await supabase
        .from('parte_trabajo_documentos')
        .delete()
        .eq('id', documento.id);

      if (error) throw error;

      toast({
        title: "Documento eliminado",
        description: "El documento se ha eliminado correctamente",
      });

      fetchDocumentos();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({
        title: "Error",
        description: "Error al eliminar el documento",
        variant: "destructive",
      });
    }
  };

  const getDocumentIcon = (tipo: string) => {
    const tipoInfo = TIPOS_DOCUMENTO.find(t => t.value === tipo);
    const IconComponent = tipoInfo?.icon || File;
    return <IconComponent className="h-4 w-4" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const isImageFile = (filename: string) => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
    return imageExtensions.some(ext => filename.toLowerCase().endsWith(ext));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Upload Section - Only show if not readonly */}
      {!readonly && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Subir Documento/Foto
            </CardTitle>
            <CardDescription>
              Adjunta fotos antes/después del trabajo, planos, facturas y otros documentos
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo de documento</Label>
                <Select value={tipoDocumento} onValueChange={setTipoDocumento}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS_DOCUMENTO.map((tipo) => (
                      <SelectItem key={tipo.value} value={tipo.value}>
                        <div className="flex items-center gap-2">
                          <tipo.icon className="h-4 w-4" />
                          {tipo.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="descripcion">Descripción (opcional)</Label>
                <Input
                  id="descripcion"
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                  placeholder="Descripción del documento..."
                />
              </div>
            </div>
            
            {/* Tactile buttons for camera and gallery */}
            <div className="grid grid-cols-2 gap-3">
              <Button
                type="button"
                variant="outline"
                className="h-14 flex flex-col items-center justify-center gap-1"
                onClick={() => cameraInputRef.current?.click()}
                disabled={!tipoDocumento || uploading}
              >
                <Camera className="h-5 w-5" />
                <span className="text-xs">Hacer Foto</span>
              </Button>
              
              <Button
                type="button"
                variant="outline"
                className="h-14 flex flex-col items-center justify-center gap-1"
                onClick={() => galleryInputRef.current?.click()}
                disabled={!tipoDocumento || uploading}
              >
                <ImagePlus className="h-5 w-5" />
                <span className="text-xs">Elegir Archivo</span>
              </Button>
            </div>

            {/* Hidden inputs */}
            <input
              type="file"
              ref={cameraInputRef}
              onChange={handleFileUpload}
              className="hidden"
              accept="image/*"
              capture="environment"
            />
            <input
              type="file"
              ref={galleryInputRef}
              onChange={handleFileUpload}
              className="hidden"
              accept="image/*,.pdf,.doc,.docx,.txt"
            />
            
            {(uploading || optimizing) && (
              <div className="flex items-center justify-center gap-2 p-2 bg-muted rounded-lg">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                <span className="text-sm text-muted-foreground">{optimizing ? 'Optimizando imagen…' : 'Subiendo…'}</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Documents List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FolderOpen className="h-5 w-5" />
            Documentos Adjuntos
            {documentos.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {documentos.length}
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            {documentos.length === 0 
              ? "No hay documentos adjuntos a este parte de trabajo"
              : `${documentos.length} documento${documentos.length !== 1 ? 's' : ''} adjunto${documentos.length !== 1 ? 's' : ''}`
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {documentos.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <File className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No hay documentos adjuntos</p>
              {!readonly && (
                <p className="text-sm">Selecciona un tipo de documento y sube el primer archivo</p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {documentos.map((documento) => (
                <div key={documento.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    {getDocumentIcon(documento.tipo_documento)}
                    <div>
                      <p className="font-medium text-sm">{documento.nombre_archivo}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Badge variant="outline" className="text-xs">
                          {TIPOS_DOCUMENTO.find(t => t.value === documento.tipo_documento)?.label || documento.tipo_documento}
                        </Badge>
                        <span>{formatFileSize(documento.tamaño_archivo)}</span>
                        <span>{new Date(documento.created_at).toLocaleDateString()}</span>
                      </div>
                      {documento.descripcion && (
                        <p className="text-xs text-muted-foreground mt-1">{documento.descripcion}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {/* Preview button for images */}
                    {isImageFile(documento.nombre_archivo) && (
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm">
                            <Image className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-3xl">
                          <DialogHeader>
                            <DialogTitle>{documento.nombre_archivo}</DialogTitle>
                          </DialogHeader>
                          <div className="flex justify-center">
                            <img 
                              src={signedUrls[documento.id] || ''} 
                              alt={documento.nombre_archivo}
                              className="max-w-full max-h-[70vh] object-contain"
                            />
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleView(documento)}
                      title="Ver en nueva pestaña"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(documento)}
                      title="Descargar"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    
                    {!readonly && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="destructive" size="sm">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>¿Eliminar documento?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Esta acción no se puede deshacer. El documento "{documento.nombre_archivo}" se eliminará permanentemente.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => handleDelete(documento)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Eliminar
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};